import dayjs from 'dayjs';
import { lazy, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { isMobile } from 'react-device-detect';
import { CSSTransition } from 'react-transition-group';

import TextButton from '@/components/elements/Button/TextButton';
import { MatchResultInfo } from '@/components/layouts/Modal/MatchDetail';
import RacerNameData from '@/components/layouts/Race/RaceCard/RacerNameData';
import useModal from '@/hooks/modal/useModal';
import {
  EntryRacer,
  EntryRacerNote,
  EntryRacerScore,
  EntryRacerScoreAttByCond,
  EntryRacerScoreAttributeDetail,
  EntryRacerScoreDetailOwn,
  MultiraceType,
  Race,
  RaceProgram,
  ReleaseStatus,
} from '@/lib/apollo/types/gen/graphql';
import { RaceInfoType, ScoreCsvIndex } from '@/modelTypes';
import { forecastMarkSelector } from '@/selectors/forecastMark';
import { defaultTheme } from '@/utility/theme';
import { toHalfWidth } from '@/utility/utils';

import ModalBase from '../../Modal/Base';
import BracketNumberData from './BracketNumberData';
import OverlayTable from './OverlayTable';
import HighlightValue from './Partials/HighlightValue';
import TableData from './Partials/TableData';
import TableHeader from './Partials/TableHeader';
import PercentageOfBetsData from './PercentageOfBetsData';
import RacerScoreDetailData from './RacerScoreDetailData';
import RecentResultData from './RecentResultData';
import {
  ArrivalOrderData,
  BicycleNumberData,
  BorderData,
  BorderHeader,
  Container,
  GearRatioBefore,
  MarkSymbol,
  RacerNameHeader,
  ScrollWrapper,
  Table,
  TableRow,
  WinningAvarageHeader,
} from './style';

const MatchDetail = lazy(
  () => import('@/components/layouts/Modal/MatchDetail'),
);

interface RaceCardProps {
  raceProgram: RaceProgram;
  heldDate: dayjs.Dayjs;
  isFirstDay: boolean;
  entryRacers: EntryRacer[];
  entryRacerNoteList?: EntryRacerNote[];
  showEntryRacerNoteModal?: (entryRacer: EntryRacer) => void;
  raceInfoType: RaceInfoType;
  multiraceType?: MultiraceType;
  scrollable?: boolean;
  scoreDetailOwns?: EntryRacerScoreDetailOwn[];
  scoreDetailMatchByBasic?: EntryRacerScoreDetailOwn[];
  scoreDetailMatchByLine?: EntryRacerScoreDetailOwn[];
  scoreByAttribute?: EntryRacerScoreAttByCond[];
  lineForecast?: NonNullable<Race['lineForecast']>;
}

export type MatchRecordHeader = {
  registrationNum: number;
  shortName: string;
};

export type MatchRecordResult = {
  registrationNum: number;
  totalWinCnt: number;
  totalLoseCnt: number;
  matchResult: { [key: number]: string };
  matchResultDetail: { [key: number]: MatchResultInfo[] };
};

export type SameLineMatchResult = {
  registrationNum: number;
  entryCount: number;
  topCnt: number;
  rank2Count: number;
  rank3Count: number;
  unplacedCount: number;
  matchResult: { [key: number]: string };
  matchResultDetail: { [key: number]: MatchResultInfo[] };
};

export enum ScoreCsvEnum {
  ScoreCsv1 = 'scoreCsv1',
  ScoreCsv2 = 'scoreCsv2',
  ScoreCsv3 = 'scoreCsv3',
  ScoreCsv4 = 'scoreCsv4',
  ScoreCsv5 = 'scoreCsv5',
  ScoreCsv6 = 'scoreCsv6',
  ScoreCsv7 = 'scoreCsv7',
}

const RaceCard: React.FC<RaceCardProps> = ({
  raceProgram,
  heldDate,
  isFirstDay,
  entryRacers,
  entryRacerNoteList,
  showEntryRacerNoteModal,
  raceInfoType,
  multiraceType,
  scrollable,
  scoreDetailOwns,
  scoreDetailMatchByBasic,
  scoreDetailMatchByLine,
  scoreByAttribute,
  lineForecast,
}) => {
  const baseRef = useRef<HTMLTableElement | null>(null);
  const targetRef = useRef<HTMLTableHeaderCellElement | null>(null);

  const [isActive, setIsActive] = useState<boolean>(false);
  const [openMatchDetail, setOpenMatchDetail] = useModal(false);

  const convertToNumArray = (key: keyof EntryRacerScore): number[] =>
    entryRacers.map(er => Number(er.entryRacerScore[key]));

  const isResult = useMemo(
    () => raceInfoType === RaceInfoType.Result,
    [raceInfoType],
  );
  const isScore = useMemo(
    () => raceInfoType === RaceInfoType.Score,
    [raceInfoType],
  );
  const isVelodromeResult = useMemo(
    () => raceInfoType === RaceInfoType.VelodromeResult,
    [raceInfoType],
  );

  const isRecentResult = useMemo(
    () => raceInfoType === RaceInfoType.RecentResult,
    [raceInfoType],
  );
  const isNone = useMemo(
    () => raceInfoType === RaceInfoType.None,
    [raceInfoType],
  );
  const isLine = useMemo(
    () => raceInfoType === RaceInfoType.Line,
    [raceInfoType],
  );
  const isBasic = useMemo(
    () => raceInfoType === RaceInfoType.Basic,
    [raceInfoType],
  );
  const isEmpty = useMemo(
    () => raceInfoType === RaceInfoType.Empty,
    [raceInfoType],
  );
  const lineGroupList = useMemo(() => {
    if (lineForecast) {
      let tempBicycleGroup = '';
      let lineGroup: string[] = [];
      lineForecast.forEach(lf => {
        lf.forEach(l => {
          if (l) {
            if (tempBicycleGroup) {
              tempBicycleGroup = tempBicycleGroup + '_';
            }
            tempBicycleGroup = tempBicycleGroup + String(l);
          } else {
            if (tempBicycleGroup) {
              lineGroup.push(tempBicycleGroup);
              tempBicycleGroup = '';
            }
          }
        });
      });
      return lineGroup;
    }
    return [];
  }, [lineForecast]);

  const onScroll = () => {
    if (targetRef.current && baseRef.current) {
      setIsActive(
        baseRef.current.scrollLeft > targetRef.current.scrollLeft + 90,
      );
    }
  };

  const filterScoreDetailOwns = (registrationNum: number) => {
    if (scoreDetailOwns) {
      const temp = scoreDetailOwns.filter(
        item => item.registrationNum == registrationNum,
      );
      if (
        !isFirstDay &&
        temp.length != 0 &&
        temp[0].heldFirstDate.format('YYYY-MM-DD') !=
          raceProgram.heldFirstDate.format('YYYY-MM-DD')
      ) {
        // 補充選手：「開催の初日じゃない」「成績の同一開催初日のデータがない」
        // ダミーデータを先頭に追加する
        temp.unshift({
          averageRaceScore: 0,
          created: raceProgram.heldFirstDate,
          entryRacer: [],
          gradeName: raceProgram.gradeName,
          heldFirstDate: raceProgram.heldFirstDate,
          registrationNum: 0,
          scoreCsv1: '',
          scoreCsv2: '',
          scoreCsv3: '',
          scoreCsv4: '',
          scoreCsv5: '',
          scoreCsv6: '',
          scoreCsv7: '',
          updated: raceProgram.heldFirstDate,
          velodromeCode: raceProgram.velodromeCode,
        });
      }
      return temp;
    }
    return [];
  };

  const filterScoreDetailMatch = (registrationNum: number) => {
    if (isBasic && scoreDetailMatchByBasic) {
      return scoreDetailMatchByBasic.filter(
        item => item.registrationNum == registrationNum,
      );
    }
    if (isLine && scoreDetailMatchByLine) {
      return scoreDetailMatchByLine.filter(
        item => item.registrationNum == registrationNum,
      );
    }
    return [];
  };

  const filterScoreByAttribute = (registrationNum: number) => {
    if (scoreByAttribute) {
      return scoreByAttribute.filter(
        item => item.registrationNum == registrationNum,
      )[0];
    }
  };

  const convertToNumArrayByScoreAtt = (
    key: keyof EntryRacerScoreAttributeDetail,
  ): number[] => {
    if (scoreByAttribute) {
      const result: number[] = [];
      scoreByAttribute.forEach(att => {
        if (att.scoreSameLine) {
          result.push(Number(att.scoreSameLine[key]));
        }
      });
      return result;
    }
    return [];
  };

  const matchRecordHeader = useMemo(() => {
    const result: MatchRecordHeader[] = [];

    if (entryRacers) {
      entryRacers.forEach((er: EntryRacer) => {
        const tmp: MatchRecordHeader = {
          registrationNum: er.registrationNum,
          shortName: er.shortName,
        };
        result.push(tmp);
      });
    }
    return result;
  }, [entryRacers]);

  const getMatchRank = (scoreCsvA: string, scoreCsvB: string) => {
    if (scoreCsvA == '' || scoreCsvB == '') {
      return [0, 0];
    }

    // 直接対決が無ければ「0,0」を返却（CSVの4番目「レース番号」の一致有無を確認）
    const csvSplitA = scoreCsvA.split(',');
    const csvSplitB = scoreCsvB.split(',');
    if (csvSplitA[ScoreCsvIndex.RaceNum] !== csvSplitB[ScoreCsvIndex.RaceNum]) {
      return [0, 0];
    }

    // 日付が表示している開催日付以降の場合は対象外
    if (csvSplitA[ScoreCsvIndex.HeldDate] >= heldDate.format('YYYY-MM-DD')) {
      return [0, 0];
    }

    // 直接対決があれば「Aの順位, Bの順位」を返却（着順：CSVの3番目）
    const aRank = Number(csvSplitA[ScoreCsvIndex.Rank]);
    const bRank = Number(csvSplitB[ScoreCsvIndex.Rank]);

    // 「0位」同士で、ステータスも空文字同士なら「レース中止」判定なので集計しない
    if (aRank === 0 && bRank === 0) {
      if (
        csvSplitA[ScoreCsvIndex.PersonalStatus] == '' &&
        csvSplitB[ScoreCsvIndex.PersonalStatus] == ''
      ) {
        return [0, 0];
      }
    }

    // どちらかが「0位」で、「欠車」の場合は集計しない（厳密には直接対決していないので）
    if (aRank === 0 && csvSplitA[ScoreCsvIndex.PersonalStatus] == '欠') {
      return [0, 0];
    }

    if (bRank === 0 && csvSplitB[ScoreCsvIndex.PersonalStatus] == '欠') {
      return [0, 0];
    }

    // 「0位」は落車や失格等で順位がつかなかった場合なので「99位」として返却
    return [aRank == 0 ? 99 : aRank, bRank == 0 ? 99 : bRank];
  };

  const setResultInfo = (
    scoreDetail: EntryRacerScoreDetailOwn,
    argRaceDate: string,
    argRaceNum: string,
    argRaceEventName: string,
    argRankMine: number,
    argStatusMine: string,
    argRankOpponent: number,
    argStatusOpponent: string,
  ) => {
    const resultInfoBase: MatchResultInfo = {
      raceDate: argRaceDate,
      velodromeCode: scoreDetail.velodromeCode,
      grade: scoreDetail.gradeName,
      raceNum: argRaceNum,
      raceClassName: argRaceEventName,
      rankMine: argRankMine,
      statusMine: argStatusMine,
      rankOpponent: argRankOpponent,
      statusOpponent: argStatusOpponent,
    };
    return resultInfoBase;
  };

  const calcScoreCsv = (
    scoreDetailA: EntryRacerScoreDetailOwn,
    scoreDetailB: EntryRacerScoreDetailOwn,
    matchDetailA: MatchResultInfo[],
    matchDetailB: MatchResultInfo[],
  ) => {
    var countWinA = 0;
    var countWinB = 0;
    var countDraw = 0;

    // 詳細表示のとき、新しい順で表示するので「７日目 -> １日目」の順で集計する
    const targetList = [
      ScoreCsvEnum.ScoreCsv7,
      ScoreCsvEnum.ScoreCsv6,
      ScoreCsvEnum.ScoreCsv5,
      ScoreCsvEnum.ScoreCsv4,
      ScoreCsvEnum.ScoreCsv3,
      ScoreCsvEnum.ScoreCsv2,
      ScoreCsvEnum.ScoreCsv1,
    ];

    targetList.forEach((target: ScoreCsvEnum) => {
      var result = getMatchRank(scoreDetailA[target], scoreDetailB[target]);
      if (result[0] !== 0 && result[1] !== 0) {
        matchDetailA.push(
          setResultInfo(
            scoreDetailA,
            scoreDetailA[target].split(',')[ScoreCsvIndex.HeldDate],
            scoreDetailA[target].split(',')[ScoreCsvIndex.RaceNum],
            scoreDetailA[target].split(',')[ScoreCsvIndex.RaceEventName],
            result[0],
            scoreDetailA[target].split(',')[ScoreCsvIndex.PersonalStatus],
            result[1],
            scoreDetailB[target].split(',')[ScoreCsvIndex.PersonalStatus],
          ),
        );

        matchDetailB.push(
          setResultInfo(
            scoreDetailB,
            scoreDetailB[target].split(',')[ScoreCsvIndex.HeldDate],
            scoreDetailB[target].split(',')[ScoreCsvIndex.RaceNum],
            scoreDetailB[target].split(',')[ScoreCsvIndex.RaceEventName],
            result[1],
            scoreDetailB[target].split(',')[ScoreCsvIndex.PersonalStatus],
            result[0],
            scoreDetailA[target].split(',')[ScoreCsvIndex.PersonalStatus],
          ),
        );

        if (result[0] == result[1]) {
          countDraw++;
        } else if (result[0] < result[1]) {
          countWinA++;
        } else if (result[0] > result[1]) {
          countWinB++;
        }
      }
    });

    return [countWinA, countWinB, countDraw];
  };

  const getWinCount = (
    scoreListA: EntryRacerScoreDetailOwn[],
    matchTarget: number,
    matchDetailA: MatchResultInfo[],
    matchDetailB: MatchResultInfo[],
  ) => {
    const scoreListB = filterScoreDetailMatch(matchTarget);
    var winCnt = 0;
    var loseCnt = 0;
    var drawCnt = 0;

    scoreListA.forEach((si: EntryRacerScoreDetailOwn) => {
      const sameRace = scoreListB.filter(
        item =>
          item.heldFirstDate.toString() === si.heldFirstDate.toString() &&
          item.velodromeCode === si.velodromeCode,
      ); // 同一開催初日 + 同一競輪場 のデータを取得

      if (sameRace.length == 1) {
        // CSV内の比較処理
        const matchResult = calcScoreCsv(
          si,
          sameRace[0],
          matchDetailA,
          matchDetailB,
        );
        winCnt = winCnt + matchResult[0];
        loseCnt = loseCnt + matchResult[1];
        drawCnt = drawCnt + matchResult[2];
      }
    });

    return [winCnt, loseCnt, drawCnt];
  };

  const getTopCount = (
    scoreListA: EntryRacerScoreDetailOwn[],
    matchTarget: number,
    matchDetailA: MatchResultInfo[],
    matchDetailB: MatchResultInfo[],
  ) => {
    const scoreListB = filterScoreDetailMatch(matchTarget);
    var aTopCnt = 0;
    var aRank2Count = 0;
    var aRank3Count = 0;
    var aUnplacedCount = 0;
    var bTopCnt = 0;
    var bRank2Count = 0;
    var bRank3Count = 0;
    var bUnplacedCount = 0;

    // 詳細表示のとき、新しい順で表示するので「７日目 -> １日目」の順で集計する
    const targetList = [
      ScoreCsvEnum.ScoreCsv7,
      ScoreCsvEnum.ScoreCsv6,
      ScoreCsvEnum.ScoreCsv5,
      ScoreCsvEnum.ScoreCsv4,
      ScoreCsvEnum.ScoreCsv3,
      ScoreCsvEnum.ScoreCsv2,
      ScoreCsvEnum.ScoreCsv1,
    ];

    scoreListA.forEach((si: EntryRacerScoreDetailOwn) => {
      const sameRace = scoreListB.filter(
        item =>
          item.heldFirstDate.toString() === si.heldFirstDate.toString() &&
          item.velodromeCode === si.velodromeCode,
      ); // 同一開催初日 + 同一競輪場 のデータを取得

      if (sameRace.length == 1) {
        targetList.forEach((target: ScoreCsvEnum) => {
          if (sameRace[0][target].includes(String(si.registrationNum))) {
            var result = getMatchRank(si[target], sameRace[0][target]);
            if (result[0] != 0) {
              aTopCnt = result[0] === 1 ? aTopCnt + 1 : aTopCnt;
              aRank2Count = result[0] === 2 ? aRank2Count + 1 : aRank2Count;
              aRank3Count = result[0] === 3 ? aRank3Count + 1 : aRank3Count;
              aUnplacedCount =
                result[0] > 3 ? aUnplacedCount + 1 : aUnplacedCount;
              matchDetailA.push(
                setResultInfo(
                  si,
                  si[target].split(',')[ScoreCsvIndex.HeldDate],
                  si[target].split(',')[ScoreCsvIndex.RaceNum],
                  si[target].split(',')[ScoreCsvIndex.RaceEventName],
                  result[0],
                  si[target].split(',')[ScoreCsvIndex.PersonalStatus],
                  result[1],
                  sameRace[0][target].split(',')[ScoreCsvIndex.PersonalStatus],
                ),
              );
            }
            if (result[1] != 0) {
              bTopCnt = result[1] === 1 ? bTopCnt + 1 : bTopCnt;
              bRank2Count = result[1] === 2 ? bRank2Count + 1 : bRank2Count;
              bRank3Count = result[1] === 3 ? bRank3Count + 1 : bRank3Count;
              bUnplacedCount =
                result[1] > 3 ? bUnplacedCount + 1 : bUnplacedCount;
              matchDetailB.push(
                setResultInfo(
                  sameRace[0],
                  sameRace[0][target].split(',')[ScoreCsvIndex.HeldDate],
                  sameRace[0][target].split(',')[ScoreCsvIndex.RaceNum],
                  sameRace[0][target].split(',')[ScoreCsvIndex.RaceEventName],
                  result[1],
                  sameRace[0][target].split(',')[ScoreCsvIndex.PersonalStatus],
                  result[0],
                  si[target].split(',')[ScoreCsvIndex.PersonalStatus],
                ),
              );
            }
          }
        });
      }
    });

    return [
      aTopCnt,
      aRank2Count,
      aRank3Count,
      aUnplacedCount,
      bTopCnt,
      bRank2Count,
      bRank3Count,
      bUnplacedCount,
    ];
  };

  const setMatchRecordResult = (input: MatchRecordResult[]) => {
    for (var i = 0; i < entryRacers.length; i++) {
      const targetNumI = entryRacers[i].registrationNum;
      const scoreListI = filterScoreDetailMatch(entryRacers[i].registrationNum);

      for (var j = i; j < entryRacers.length; j++) {
        if (i === j) {
          continue; // [i==j] -> 同じ選手同士の比較なので処理スキップ
        }

        const matchDetailI: MatchResultInfo[] = [];
        const matchDetailJ: MatchResultInfo[] = [];
        const targetNumJ = entryRacers[j].registrationNum;
        const res = getWinCount(
          scoreListI,
          targetNumJ,
          matchDetailI,
          matchDetailJ,
        );

        if (res[0] !== 0 || res[1] !== 0 || res[2] !== 0) {
          input.filter(Item => {
            if (Item.registrationNum === targetNumI) {
              Item.matchResult[targetNumJ] = `${res[0]}-${res[1]}`;
              Item.matchResultDetail[targetNumJ] = matchDetailI.concat();
              Item.totalWinCnt = Item.totalWinCnt + res[0];
              Item.totalLoseCnt = Item.totalLoseCnt + res[1];
            }

            if (Item.registrationNum === targetNumJ) {
              Item.matchResult[targetNumI] = `${res[1]}-${res[0]}`;
              Item.matchResultDetail[targetNumI] = matchDetailJ.concat();
              Item.totalWinCnt = Item.totalWinCnt + res[1];
              Item.totalLoseCnt = Item.totalLoseCnt + res[0];
            }
          });
        }
      }
    }
  };

  const setSameLineMatchResult = (input: SameLineMatchResult[]) => {
    for (var i = 0; i < entryRacers.length; i++) {
      const targetNumI = entryRacers[i].registrationNum;
      const targetBicycleNum = entryRacers[i].bicycleNum;
      const scoreListI = filterScoreDetailMatch(entryRacers[i].registrationNum);
      const lineGroup = lineGroupList.filter(lg =>
        lg.includes(String(targetBicycleNum)),
      )[0];

      if (!lineGroup) {
        continue;
      }

      if (lineGroup && lineGroup.length === 1) {
        // ライングループが１件の場合、「単騎」属性なので、対戦成績の集計をしない
        continue;
      }

      for (var j = i; j < entryRacers.length; j++) {
        if (i === j) {
          continue; // [i==j] -> 同じ選手同士の比較なので処理スキップ
        }
        if (!lineGroup.includes(String(entryRacers[j].bicycleNum))) {
          // このレースでラインを組んでいない場合は、対戦成績の集計をしない
          continue;
        }

        const matchDetailI: MatchResultInfo[] = [];
        const matchDetailJ: MatchResultInfo[] = [];
        const targetNumJ = entryRacers[j].registrationNum;
        const res = getTopCount(
          scoreListI,
          targetNumJ,
          matchDetailI,
          matchDetailJ,
        );

        if (res[0] + res[1] + res[2] + res[3] != 0) {
          input.filter(Item => {
            if (Item.registrationNum === targetNumI) {
              Item.entryCount =
                Item.entryCount + res[0] + res[1] + res[2] + res[3];
              Item.topCnt = Item.topCnt + res[0];
              Item.rank2Count = Item.rank2Count + res[1];
              Item.rank3Count = Item.rank3Count + res[2];
              Item.unplacedCount = Item.unplacedCount + res[3];
              Item.matchResult[
                targetNumJ
              ] = `${res[0]}.${res[1]}.${res[2]}.${res[3]}`;
              Item.matchResultDetail[targetNumJ] = matchDetailI.concat();
            }

            if (Item.registrationNum === targetNumJ) {
              Item.entryCount =
                Item.entryCount + res[4] + res[5] + res[6] + res[7];
              Item.topCnt = Item.topCnt + res[4];
              Item.rank2Count = Item.rank2Count + res[5];
              Item.rank3Count = Item.rank3Count + res[6];
              Item.unplacedCount = Item.unplacedCount + res[7];
              Item.matchResult[
                targetNumI
              ] = `${res[4]}.${res[5]}.${res[6]}.${res[7]}`;
              Item.matchResultDetail[targetNumI] = matchDetailJ.concat();
            }
          });
        }
      }
    }
  };

  const matchRecordItems = useMemo(() => {
    const result: MatchRecordResult[] = [];

    if (entryRacers && isBasic) {
      entryRacers.forEach((er: EntryRacer) => {
        const tmp: MatchRecordResult = {
          registrationNum: er.registrationNum,
          totalWinCnt: 0,
          totalLoseCnt: 0,
          matchResult: {},
          matchResultDetail: {},
        };
        tmp.matchResult[er.registrationNum] = '-';
        result.push(tmp);
      });
    }

    setMatchRecordResult(result);
    return result;
  }, [scoreDetailMatchByBasic, entryRacers, isBasic]);

  const sameLineMatchItems = useMemo(() => {
    const result: SameLineMatchResult[] = [];

    if (entryRacers && isLine) {
      entryRacers.forEach((er: EntryRacer) => {
        const tmp: SameLineMatchResult = {
          registrationNum: er.registrationNum,
          entryCount: 0,
          topCnt: 0,
          rank2Count: 0,
          rank3Count: 0,
          unplacedCount: 0,
          matchResult: {},
          matchResultDetail: {},
        };
        tmp.matchResult[er.registrationNum] = '-';
        result.push(tmp);
      });
    }

    setSameLineMatchResult(result);
    return result;
  }, [scoreDetailMatchByLine, entryRacers, isLine]);

  const getTotalResult = (regNum: number) => {
    var res = ' - ';
    if (matchRecordItems) {
      matchRecordItems.filter(item => {
        if (item.registrationNum === regNum) {
          res = `${item.totalWinCnt}-${item.totalLoseCnt}`;
        }
      });
    }
    return res;
  };

  const getLineTotalResult = (regNum: number) => {
    var res = ' - ';
    if (isLine && matchRecordItems) {
      sameLineMatchItems.filter(item => {
        if (item.registrationNum === regNum) {
          if (
            item.topCnt +
              item.rank2Count +
              item.rank3Count +
              item.unplacedCount !=
            0
          ) {
            res = `${item.topCnt}.${item.rank2Count}.${item.rank3Count}.${item.unplacedCount}`;
          }
        }
      });
    }
    return res;
  };

  const getMatchResult = (regNumA: number, regNumB: number) => {
    if (matchRecordItems) {
      const matchResult = matchRecordItems.filter(
        item => item.registrationNum === regNumA,
      )[0].matchResult[regNumB];

      return <span style={{ textDecoration: 'underline' }}>{matchResult}</span>;
    }
    return <></>;
  };

  const getSameLineMatchResult = (regNumA: number, regNumB: number) => {
    if (matchRecordItems) {
      const matchResult = sameLineMatchItems.filter(
        item => item.registrationNum === regNumA,
      );

      if (matchResult && matchResult.length != 0) {
        return (
          <span style={{ textDecoration: 'underline' }}>
            {matchResult[0].matchResult[regNumB]}
          </span>
        );
      }
    }
    return <></>;
  };

  const isMissedRacer = (er: EntryRacer) =>
    er.releaseStatus !== ReleaseStatus.Released;

  const makeRecentResultHeader = (index: number) => {
    return (
      <TableHeader
        style={{
          width: '200px',
          textAlign: 'left',
          paddingLeft: defaultTheme.spacing.s,
        }}
        key={`result-header-${index}`}
      >
        {isFirstDay
          ? `直近${index + 1}`
          : index === 0
          ? `今回`
          : `直近${index}`}
      </TableHeader>
    );
  };

  const makeRecentResultHeaders = () => {
    const DISPLAY_COUNT = Number(import.meta.env.VITE_RACER_SCORE_DETAIL_COUNT); // 直近成績表示数（ENVから取得）
    const results = [];
    for (let i = 0; i < DISPLAY_COUNT; i++) {
      results.push(makeRecentResultHeader(i));
    }
    return results;
  };
  const makeVelodromeResultHeaders = () => {
    const DISPLAY_COUNT2 = Number(
      import.meta.env.VITE_RACER_SCORE_DETAIL_COUNT_VELODROME,
    ); // 当場成績表示数（ENVから取得）
    const results = [];
    for (let i = 0; i < DISPLAY_COUNT2; i++) {
      results.push(makeRecentResultHeader(i));
    }
    return results;
  };

  const [mineName, setMineName] = useState<string>('');
  const [opponentName, setOpponentName] = useState<string>('');
  const [matchResults, setMatchResults] = useState<MatchResultInfo[]>([]);
  const [highlightTargets, setHighlightTargets] = useState<number[]>([]);
  const [isModeRaceResult, setIsModeRaceResult] = useState<boolean>(false);
  const setMatchDetail = (regNumA: number, regNumB: number) => {
    if (matchRecordHeader && matchRecordItems) {
      matchRecordHeader.forEach((er: MatchRecordHeader) => {
        if (er.registrationNum == regNumA) {
          setMineName(er.shortName);
        }
        if (er.registrationNum == regNumB) {
          setOpponentName(er.shortName);
        }
      });

      setMatchResults(
        matchRecordItems
          .filter(item => item.registrationNum === regNumA)[0]
          .matchResultDetail[regNumB].concat(),
      );
      setHighlightTargets([regNumA, regNumB]);
      setIsModeRaceResult(false);
      setOpenMatchDetail(true);
    }
  };

  const setSameLineMatchDetail = (regNumA: number, regNumB: number) => {
    if (matchRecordHeader && sameLineMatchItems) {
      matchRecordHeader.forEach((er: MatchRecordHeader) => {
        if (er.registrationNum == regNumA) {
          setMineName(er.shortName);
        }
        if (er.registrationNum == regNumB) {
          setOpponentName(er.shortName);
        }
      });

      setMatchResults(
        sameLineMatchItems
          .filter(item => item.registrationNum === regNumA)[0]
          .matchResultDetail[regNumB].concat(),
      );
      setHighlightTargets([regNumA, regNumB]);
      setIsModeRaceResult(false);
      setOpenMatchDetail(true);
    }
  };

  const openRaceResultModal = (
    matchResult: MatchResultInfo,
    highlightTarget: number,
  ) => {
    setMineName('');
    setOpponentName('');

    const tmpHighlightTargets: number[] = [];
    tmpHighlightTargets.push(highlightTarget);
    setHighlightTargets(tmpHighlightTargets);

    const tmpMatchResult: MatchResultInfo[] = [];
    tmpMatchResult.push(matchResult);
    setMatchResults(tmpMatchResult);

    setIsModeRaceResult(true);
    setOpenMatchDetail(true);
  };

  if (isEmpty) {
    return <Container></Container>;
  }

  const commentHeaderStyle = useMemo(() => {
    if (entryRacers) {
      let maxLength = 0;
      entryRacers.forEach((er: EntryRacer) => {
        if (maxLength < er.charilotoComment?.length!) {
          maxLength = er.charilotoComment?.length!;
        }
      });

      if (isMobile) {
        // SPレイアウトの場合
        if (maxLength >= 94) {
          return { width: '500px' } as React.CSSProperties;
        } else if (maxLength >= 61) {
          return { width: '400px' } as React.CSSProperties;
        } else if (maxLength >= 16) {
          return { width: '280px' } as React.CSSProperties;
        } else if (maxLength > 0) {
          return { width: '200px' } as React.CSSProperties;
        }
      } else {
        // PCレイアウトの場合
        if (maxLength >= 61) {
          return { width: '720px' } as React.CSSProperties;
        } else if (maxLength >= 26) {
          return { width: '450px' } as React.CSSProperties;
        } else if (maxLength >= 16) {
          return { width: '400px' } as React.CSSProperties;
        } else if (maxLength > 0) {
          return { width: '200px' } as React.CSSProperties;
        }
      }
    }

    return { width: '60px' } as React.CSSProperties;
  }, [entryRacers]);

  const commentItemStyle = (comment: string) => {
    if (isMobile) {
      if (comment.length >= 144) {
        // SPの場合、144文字以上で文字ポイントを下げる（9px）
        return {
          textAlign: 'left',
          paddingLeft: '4px',
          paddingTop: '3px',
          fontSize: '9px',
        } as React.CSSProperties;
      } else if (comment.length >= 121) {
        // SPの場合、121文字以上で文字ポイントを下げる（10px）
        return {
          textAlign: 'left',
          paddingLeft: '4px',
          paddingTop: '3px',
          fontSize: '10px',
        } as React.CSSProperties;
      } else if (comment.length >= 37) {
        // SPの場合、37文字以上で文字ポイントを下げる（12px）
        return {
          textAlign: 'left',
          paddingLeft: '4px',
          paddingTop: '3px',
          fontSize: defaultTheme.font.size.xs,
        } as React.CSSProperties;
      }
    } else {
      if (comment.length >= 100) {
        // PCの場合、100文字以上で文字ポイントを下げる
        return {
          textAlign: 'left',
          paddingLeft: '4px',
          paddingTop: '3px',
          fontSize: defaultTheme.font.size.xs,
        } as React.CSSProperties;
      }
    }

    return {
      textAlign: 'left',
      paddingLeft: '4px',
      fontSize: defaultTheme.font.size.s,
    } as React.CSSProperties;
  };

  return (
    <Container>
      <CSSTransition in={!!scrollable && isActive} timeout={100} unmountOnExit>
        <OverlayTable
          entryRacers={entryRacers}
          entryRacerNoteList={entryRacerNoteList}
          showEntryRacerNoteModal={showEntryRacerNoteModal}
        />
      </CSSTransition>
      <ScrollWrapper scrollable={scrollable} ref={baseRef} onScroll={onScroll}>
        <Table>
          <thead>
            <tr>
              {isResult ? (
                <BorderHeader style={{ width: '20px' }} isBorderLeftNone>
                  着
                </BorderHeader>
              ) : (
                <>
                  {!isNone && (
                    <BorderHeader isBorderLeftNone style={{ width: '33px' }}>
                      予想
                    </BorderHeader>
                  )}
                  <BorderHeader style={{ width: '20px' }}>枠</BorderHeader>
                </>
              )}
              <BorderHeader style={{ width: '20px' }} ref={targetRef}>
                車
              </BorderHeader>
              <RacerNameHeader isBorderRightNone={isNone} isNone={isNone}>
                選手名
              </RacerNameHeader>
              {!isNone && (
                <>
                  {isScore && (
                    <>
                      <TableHeader style={{ width: '33px' }}>期別</TableHeader>
                      <TableHeader style={{ width: '33px' }}>級班</TableHeader>
                      <BorderHeader
                        style={{ width: '60px' }}
                        isBorderRightNone={isScore}
                      >
                        競走得点
                      </BorderHeader>
                    </>
                  )}
                  {!(
                    isRecentResult ||
                    isResult ||
                    isScore ||
                    isVelodromeResult ||
                    isLine
                  ) && (
                    <>
                      <TableHeader style={{ width: '22px' }}>S</TableHeader>
                      <TableHeader style={{ width: '22px' }}>B</TableHeader>
                      <TableHeader style={{ width: '22px' }}>逃</TableHeader>
                      <TableHeader style={{ width: '22px' }}>捲</TableHeader>
                      <TableHeader style={{ width: '22px' }}>差</TableHeader>
                      <BorderHeader style={{ width: '22px' }}>マ</BorderHeader>

                      <TableHeader style={{ width: '51px' }}>勝率</TableHeader>
                      <TableHeader style={{ width: '51px' }}>
                        連対率
                      </TableHeader>
                      <BorderHeader style={{ width: '60px' }}>
                        3連対率
                      </BorderHeader>

                      <TableHeader style={{ width: '33px' }}>1着</TableHeader>
                      <TableHeader style={{ width: '33px' }}>2着</TableHeader>
                      <TableHeader style={{ width: '33px' }}>3着</TableHeader>
                      <BorderHeader style={{ width: '39px' }}>
                        着外
                      </BorderHeader>

                      <TableHeader style={{ width: '40px' }}>脚</TableHeader>
                      <BorderHeader style={{ width: '40px' }}>
                        ギア
                      </BorderHeader>
                      <BorderHeader style={commentHeaderStyle}>
                        コメント
                      </BorderHeader>

                      {isBasic && matchRecordHeader && entryRacers && (
                        <>
                          <TableHeader style={{ width: '60px' }}>
                            対戦成績
                          </TableHeader>
                          {matchRecordHeader.map((item, idx) => (
                            <TableHeader style={{ width: '51px' }} key={idx}>
                              {item.shortName}
                            </TableHeader>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              {isRecentResult && <>{makeRecentResultHeaders()}</>}
              {isVelodromeResult && <>{makeVelodromeResultHeaders()}</>}
              {isLine && (
                <>
                  <TableHeader style={{ width: '60px' }}>並び順</TableHeader>
                  <TableHeader style={{ width: '51px' }}>勝率</TableHeader>
                  <TableHeader style={{ width: '51px' }}>連対率</TableHeader>
                  <BorderHeader style={{ width: '60px' }}>3連対率</BorderHeader>
                  <TableHeader style={{ width: '33px' }}>1着</TableHeader>
                  <TableHeader style={{ width: '33px' }}>2着</TableHeader>
                  <TableHeader style={{ width: '33px' }}>3着</TableHeader>
                  <BorderHeader style={{ width: '39px' }}>着外</BorderHeader>

                  <TableHeader style={{ width: '86px' }}>今回</TableHeader>
                  {entryRacers &&
                    entryRacers.map((er, idx) => (
                      <TableHeader style={{ width: '60px' }} key={idx}>
                        {er.shortName}
                      </TableHeader>
                    ))}
                </>
              )}
              {multiraceType && !isRecentResult && !isVelodromeResult && (
                <WinningAvarageHeader isBorderRightNone>
                  状況/支持率
                </WinningAvarageHeader>
              )}
              {isResult && (
                <>
                  <TableHeader style={{ width: '33px' }}>着差</TableHeader>
                  <TableHeader style={{ width: '33px' }}>上り</TableHeader>
                  <TableHeader style={{ width: '33px' }}>決</TableHeader>
                  <BorderHeader
                    style={{ width: '33px' }}
                    isBorderRightNone={isResult}
                  >
                    B/S
                  </BorderHeader>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {entryRacers &&
              entryRacers.map((er, eri) => (
                <TableRow
                  key={eri}
                  bicycleNum={isResult ? 0 : er.bicycleNum}
                  disabled={isMissedRacer(er)}
                >
                  {isResult ? (
                    <ArrivalOrderData arrivalOrder={er.arrivalOrder!}>
                      {isMissedRacer(er)
                        ? '欠'
                        : er.arrivalOrder || er.playerStatus}
                    </ArrivalOrderData>
                  ) : (
                    <>
                      {!isNone && (
                        <BorderData
                          isBorderLeftNone
                          style={{
                            backgroundColor: isMissedRacer(er)
                              ? defaultTheme.background.darkGray
                              : 'inherit',
                            fontFamily: 'sans-serif',
                          }}
                        >
                          <MarkSymbol
                            path={
                              forecastMarkSelector(er.forecastMark).markSymbol
                            }
                          />
                        </BorderData>
                      )}
                      <BracketNumberData
                        bracketNum={er.bracketNum}
                        entryRacers={entryRacers}
                        index={eri}
                        isRecentResult={isRecentResult}
                        isVelodromeResult={isVelodromeResult}
                      />
                    </>
                  )}
                  <BicycleNumberData
                    bicycleNum={er.bicycleNum}
                    disabled={isMissedRacer(er)}
                  >
                    {er.bicycleNum}
                  </BicycleNumberData>
                  <RacerNameData
                    entryRacer={er}
                    raceInfoType={raceInfoType}
                    averageRaceScoreNumArray={convertToNumArray(
                      'averageRaceScore',
                    )}
                    entryRacerNoteList={entryRacerNoteList}
                    showEntryRacerNoteModal={showEntryRacerNoteModal}
                  />
                  {!isNone && (
                    <>
                      {isScore && (
                        <>
                          <TableData>{er.graduated}</TableData>
                          <TableData>
                            {toHalfWidth(`${er.gradeName}${er.unitName}`)}
                          </TableData>
                          <BorderData isBorderRightNone={isScore}>
                            <HighlightValue
                              numArray={convertToNumArray('averageRaceScore')}
                              fractionDigits={2}
                            >
                              {er.entryRacerScore.averageRaceScore}
                            </HighlightValue>
                          </BorderData>
                        </>
                      )}
                      {!(
                        isRecentResult ||
                        isResult ||
                        isScore ||
                        isVelodromeResult ||
                        isLine
                      ) && (
                        <>
                          <TableData>
                            {er.entryRacerScore.standingCount}
                          </TableData>
                          <TableData>
                            <HighlightValue
                              numArray={convertToNumArray('backCount')}
                            >
                              {er.entryRacerScore.backCount}
                            </HighlightValue>
                          </TableData>
                          <TableData>
                            {er.entryRacerScore.pacesetterCount}
                          </TableData>
                          <TableData>
                            {er.entryRacerScore.comeFromBehindCount}
                          </TableData>
                          <TableData>
                            {er.entryRacerScore.lookerCount}
                          </TableData>
                          <BorderData>
                            {er.entryRacerScore.markCount}
                          </BorderData>

                          <TableData>
                            <HighlightValue
                              numArray={convertToNumArray('winningAverage')}
                              fractionDigits={1}
                            >
                              {er.entryRacerScore.winningAverage}
                            </HighlightValue>
                          </TableData>
                          <TableData>
                            <HighlightValue
                              numArray={convertToNumArray('top2Ratio')}
                              fractionDigits={1}
                            >
                              {er.entryRacerScore.top2Ratio}
                            </HighlightValue>
                          </TableData>
                          <BorderData>
                            <HighlightValue
                              numArray={convertToNumArray('top3Ratio')}
                              fractionDigits={1}
                            >
                              {er.entryRacerScore.top3Ratio}
                            </HighlightValue>
                          </BorderData>

                          <TableData>{er.entryRacerScore.topCount}</TableData>
                          <TableData>{er.entryRacerScore.rank2Count}</TableData>
                          <TableData>{er.entryRacerScore.rank3Count}</TableData>
                          <BorderData>
                            {er.entryRacerScore.unplacedCount}
                          </BorderData>

                          <TableData>{er.racingStyle}</TableData>
                          <BorderData>
                            {er.gearRatioBefore && (
                              <GearRatioBefore>
                                {er.gearRatioBefore}
                              </GearRatioBefore>
                            )}
                            {er.gearRatio}
                          </BorderData>

                          <BorderData
                            style={commentItemStyle(er.charilotoComment || '')}
                          >
                            {er.charilotoComment || ''}
                          </BorderData>

                          <TableData>
                            {getTotalResult(er.registrationNum)}
                          </TableData>
                          {entryRacers.map((er2, idx) => (
                            <TableData key={idx}>
                              {er.registrationNum === er2.registrationNum ? (
                                <>-</>
                              ) : (
                                <TextButton
                                  size="s"
                                  type="button"
                                  onClick={() => {
                                    setMatchDetail(
                                      er.registrationNum,
                                      er2.registrationNum,
                                    );
                                  }}
                                >
                                  <span>
                                    {getMatchResult(
                                      er.registrationNum,
                                      er2.registrationNum,
                                    )}
                                  </span>
                                </TextButton>
                              )}
                            </TableData>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  {isRecentResult && er.entryRacerScore.entryRacerScoreDetails && (
                    <>
                      <RecentResultData
                        scoreDetailOwns={filterScoreDetailOwns(
                          er.registrationNum,
                        )}
                        viewHeldDate={heldDate}
                        isFirstDay={isFirstDay}
                        isVelodromeResult={isVelodromeResult}
                        openRaceResultModal={openRaceResultModal}
                      />
                    </>
                  )}
                  {isVelodromeResult &&
                    er.entryRacerScore.entryRacerScoreDetails && (
                      <>
                        <RecentResultData
                          scoreDetailOwns={filterScoreDetailOwns(
                            er.registrationNum,
                          )}
                          viewHeldDate={heldDate}
                          isFirstDay={isFirstDay}
                          isVelodromeResult={isVelodromeResult}
                          openRaceResultModal={openRaceResultModal}
                        />
                      </>
                    )}
                  {isLine && (
                    <>
                      <RacerScoreDetailData
                        forecast={
                          filterScoreByAttribute(er.registrationNum)?.forecast
                        }
                        scoreByAttribute={
                          filterScoreByAttribute(er.registrationNum)
                            ?.scoreSameLine!
                        }
                        winningAverageScores={convertToNumArrayByScoreAtt(
                          'winningAverage',
                        )}
                        top2RatioScores={convertToNumArrayByScoreAtt(
                          'top2Ratio',
                        )}
                        top3RatioScores={convertToNumArrayByScoreAtt(
                          'top3Ratio',
                        )}
                      />

                      <TableData>
                        {getLineTotalResult(er.registrationNum)}
                      </TableData>
                      {entryRacers.map((er2, idx) => (
                        <TableData key={idx}>
                          {er.registrationNum === er2.registrationNum ? (
                            <>-</>
                          ) : (
                            <TextButton
                              size="s"
                              type="button"
                              onClick={() => {
                                setSameLineMatchDetail(
                                  er.registrationNum,
                                  er2.registrationNum,
                                );
                              }}
                            >
                              <span>
                                {getSameLineMatchResult(
                                  er.registrationNum,
                                  er2.registrationNum,
                                )}
                              </span>
                            </TextButton>
                          )}
                        </TableData>
                      ))}
                    </>
                  )}
                  {multiraceType && !isRecentResult && !isVelodromeResult && (
                    <PercentageOfBetsData
                      bicycleNum={er.bicycleNum}
                      multiracePercentageOfBets={er.multiracePercentageOfBets}
                      singleRacePercentageOfBets={er.singleRacePercentageOfBets}
                      multiraceType={multiraceType}
                    />
                  )}
                  {isResult && (
                    <>
                      <TableData>{er.marginInfo}</TableData>
                      <TableData>
                        {er.arrivalOrder! > 0 &&
                          Boolean(er.finalStagePlayerTime) &&
                          er.finalStagePlayerTime!.toFixed(1)}
                      </TableData>
                      <TableData>{er.decidingFactor}</TableData>
                      <BorderData isBorderRightNone={isResult}>
                        <>
                          {er.isBackGet && er.isStandingGet && 'B S'}
                          {er.isBackGet && !er.isStandingGet && 'B'}
                          {!er.isBackGet && er.isStandingGet && 'S'}
                        </>
                      </BorderData>
                    </>
                  )}
                </TableRow>
              ))}
          </tbody>
        </Table>
      </ScrollWrapper>
      <ModalBase
        isOpen={openMatchDetail}
        handleClose={() => setOpenMatchDetail(false)}
        desktopWidth="840px"
        mobileWidth="343px"
        desktopHeight="626px"
        mobileHeight="648px"
        backgroundColor={defaultTheme.background.white}
      >
        <MatchDetail
          mineName={mineName}
          opponentName={opponentName}
          matchResults={matchResults}
          highlightTargets={highlightTargets}
          isModeRaceResult={isModeRaceResult}
        />
      </ModalBase>
    </Container>
  );
};

export default RaceCard;
